import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  integrationSchema,
  createIntegrationSchema,
  updateIntegrationSchema,
  webhookSchema,
  createWebhookSchema,
  updateWebhookSchema,
  webhookDeliverySchema,
  testWebhookSchema,
} from '../schemas/integrations.schemas';
import { errorResponseSchema, successResponseSchema } from '../schemas/common.schemas';

const c = initContract();

export const integrationsContract = c.router({
  // Integrations
  listIntegrations: {
    method: 'GET',
    path: '/integrations',
    responses: {
      200: z.object({
        integrations: z.array(integrationSchema),
      }),
      401: errorResponseSchema,
    },
    summary: 'List all integrations',
  },
  getIntegration: {
    method: 'GET',
    path: '/integrations/:id',
    pathParams: z.object({
      id: z.string().uuid(),
    }),
    responses: {
      200: integrationSchema,
      404: errorResponseSchema,
      401: errorResponseSchema,
    },
    summary: 'Get integration by ID',
  },
  createIntegration: {
    method: 'POST',
    path: '/integrations',
    body: createIntegrationSchema,
    responses: {
      201: integrationSchema,
      400: errorResponseSchema,
      401: errorResponseSchema,
    },
    summary: 'Create a new integration',
  },
  updateIntegration: {
    method: 'PATCH',
    path: '/integrations/:id',
    pathParams: z.object({
      id: z.string().uuid(),
    }),
    body: updateIntegrationSchema,
    responses: {
      200: integrationSchema,
      404: errorResponseSchema,
      400: errorResponseSchema,
      401: errorResponseSchema,
    },
    summary: 'Update integration',
  },
  deleteIntegration: {
    method: 'DELETE',
    path: '/integrations/:id',
    pathParams: z.object({
      id: z.string().uuid(),
    }),
    body: z.object({}),
    responses: {
      200: successResponseSchema,
      404: errorResponseSchema,
      401: errorResponseSchema,
    },
    summary: 'Delete integration',
  },
  syncIntegration: {
    method: 'POST',
    path: '/integrations/:id/sync',
    pathParams: z.object({
      id: z.string().uuid(),
    }),
    body: z.object({}),
    responses: {
      200: integrationSchema,
      404: errorResponseSchema,
      401: errorResponseSchema,
    },
    summary: 'Trigger manual sync for integration',
  },

  // Webhooks
  listWebhooks: {
    method: 'GET',
    path: '/webhooks',
    responses: {
      200: z.object({
        webhooks: z.array(webhookSchema),
      }),
      401: errorResponseSchema,
    },
    summary: 'List all webhooks',
  },
  getWebhook: {
    method: 'GET',
    path: '/webhooks/:id',
    pathParams: z.object({
      id: z.string().uuid(),
    }),
    responses: {
      200: webhookSchema,
      404: errorResponseSchema,
      401: errorResponseSchema,
    },
    summary: 'Get webhook by ID',
  },
  createWebhook: {
    method: 'POST',
    path: '/webhooks',
    body: createWebhookSchema,
    responses: {
      201: webhookSchema,
      400: errorResponseSchema,
      401: errorResponseSchema,
    },
    summary: 'Create a new webhook',
  },
  updateWebhook: {
    method: 'PATCH',
    path: '/webhooks/:id',
    pathParams: z.object({
      id: z.string().uuid(),
    }),
    body: updateWebhookSchema,
    responses: {
      200: webhookSchema,
      404: errorResponseSchema,
      400: errorResponseSchema,
      401: errorResponseSchema,
    },
    summary: 'Update webhook',
  },
  deleteWebhook: {
    method: 'DELETE',
    path: '/webhooks/:id',
    pathParams: z.object({
      id: z.string().uuid(),
    }),
    body: z.object({}),
    responses: {
      200: successResponseSchema,
      404: errorResponseSchema,
      401: errorResponseSchema,
    },
    summary: 'Delete webhook',
  },
  testWebhook: {
    method: 'POST',
    path: '/webhooks/:id/test',
    pathParams: z.object({
      id: z.string().uuid(),
    }),
    body: testWebhookSchema,
    responses: {
      200: successResponseSchema,
      404: errorResponseSchema,
      400: errorResponseSchema,
      401: errorResponseSchema,
    },
    summary: 'Test webhook with sample payload',
  },
  getWebhookDeliveries: {
    method: 'GET',
    path: '/webhooks/:id/deliveries',
    pathParams: z.object({
      id: z.string().uuid(),
    }),
    query: z.object({
      limit: z.coerce.number().int().positive().max(100).default(20),
    }),
    responses: {
      200: z.object({
        deliveries: z.array(webhookDeliverySchema),
      }),
      404: errorResponseSchema,
      401: errorResponseSchema,
    },
    summary: 'Get webhook delivery history',
  },
});
